import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { personalInfo, projects } from '../data/mock';

const HOME_ORDER = [
  'denial-review-workbench',
  'vifei-suite',
  'agent-cassette',
  'flow-profile',
  'aliveca',
  'mission-relay',
  'ithi-vscode',
  'helios',
];

const Home = () => {
  const featuredProjects = HOME_ORDER
    .map(id => projects.find(p => p.id === id))
    .filter(Boolean);

  return (
    <main id="main-content" className="pt-16 sm:pt-20">
      {/* Hero Section */}
      <section 
        className="min-h-[75vh] sm:min-h-[85vh] flex items-center border-b border-border relative"
      >
        <div className="w-full max-w-[1040px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 relative z-10">
          <div className="max-w-[800px] space-y-6 sm:space-y-8">
            {/* Eyebrow */}
            <div className="inline-block px-3 py-1 text-xs font-medium tracking-wider uppercase border border-border rounded-full text-muted-foreground">
              {personalInfo.location} • {personalInfo.remote}
            </div>
            
            {/* H1 with hand-drawn underline */}
            <h1 className="text-[clamp(32px,6vw,56px)] leading-[1.15] font-bold tracking-[-0.01em]">
              {personalInfo.name.split(' ')[0]}{' '}
              <span className="inline-block relative">
                {personalInfo.name.split(' ')[1]}
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 120 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M2 8c8-2 16-3 24-2.5 8 .5 16 2 24 2s16-1.5 24-2c8-.5 16 0 24 1.5 4 .8 8 1.5 12 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="text-accent"
                  />
                </svg>
              </span>
            </h1>
            
            {/* One-line support */}
            <p className="text-[clamp(20px,3vw,28px)] text-muted-foreground leading-[1.4] font-medium">
              {personalInfo.tagline}
            </p>
            
            {/* Bio */}
            <p className="text-base sm:text-lg text-muted-foreground leading-[1.6] max-w-[68ch]">
              {personalInfo.bio}
            </p>
            
            {/* Two CTAs with 44px min tap targets */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                asChild 
                size="lg" 
                className="group bg-accent hover:bg-accent-strong hover:scale-105 active:scale-95 text-accent-foreground font-medium transition-all duration-[var(--dur-2)] ease-[var(--easing)] min-h-[44px]"
              >
                <Link to="/projects">
                  View projects
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-[var(--dur-2)]" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg"
                className="font-medium hover:scale-105 active:scale-95 transition-all duration-[var(--dur-2)] ease-[var(--easing)] min-h-[44px]"
              >
                <Link to="/contact">Get in touch</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Selected Work Section */}
      <section className="py-16 sm:py-24 lg:py-32">
        <div className="w-full max-w-[1040px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-[clamp(24px,4.5vw,36px)] font-bold tracking-[-0.01em] mb-4">
              Selected work
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-[68ch]">
              Recent projects across AI tools, full-stack development, and real-time systems.
            </p>
          </div>
          
          <div className="space-y-6">
            {featuredProjects.map((project) => {
              const isCaseStudy = project.id === 'ithi-vscode' || project.id === 'zodia';
              const linkTo = isCaseStudy ? '/case-study/' + project.id : '/projects';
              
              return (
                <Link
                  key={project.id}
                  to={linkTo}
                  className="block group hover:scale-[1.02] transition-transform duration-[var(--dur-2)]"
                >
                <div className="border border-border rounded-[var(--r2)] p-4 sm:p-6 lg:p-8 bg-card hover:border-accent/40 transition-all duration-[var(--dur-2)] ease-[var(--easing)]">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-medium text-muted-foreground tracking-wider uppercase">
                          {project.category}
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">{project.year}</span>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold tracking-[-0.01em] group-hover:text-accent transition-colors duration-[var(--dur-2)]">
                        {project.title}
                      </h3>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all duration-[var(--dur-2)] flex-shrink-0" />
                  </div>
                  
                  {/* Prominent outcome as one-liner */}
                  <p className="text-base sm:text-lg font-medium mb-3 leading-[1.6]">
                    {project.outcome}
                  </p>
                  
                  {project.metrics && (
                    <p className="text-sm text-accent font-medium mb-4">
                      {project.metrics}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    {project.tags.slice(0, 4).map((tag, tagIdx) => (
                      <span 
                        key={tagIdx}
                        className="text-xs px-2 py-1 rounded-md bg-secondary/50 text-secondary-foreground border border-border/50"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
          
          <div className="mt-12 text-center">
            <Button 
              asChild 
              variant="outline" 
              size="lg"
              className="font-medium min-h-[44px]"
            >
              <Link to="/projects">View all projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 lg:py-32 border-t border-border">
        <div className="w-full max-w-[1040px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-[600px] mx-auto space-y-6">
            <h2 className="text-[clamp(28px,5vw,42px)] font-bold tracking-[-0.01em]">
              Let's build something
            </h2>
            <p className="text-lg text-muted-foreground leading-[1.6]">
              Open to new opportunities and collaborations.
            </p>
            <Button 
              asChild 
              size="lg"
              className="group bg-accent hover:bg-accent-strong text-accent-foreground font-medium min-h-[44px]"
            >
              <Link to="/contact">
                Start a conversation
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-[var(--dur-2)]" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;